/**
 * Visitor Feature — Raw API functions
 *
 * These functions call the backend directly via the Axios client.
 * They are consumed by React Query hooks in hooks.ts.
 * Business logic belongs in the hooks/services layer — not here.
 */
import { apiClient } from '@/services/api/client'
import { API_ENDPOINTS } from '@/services/api/endpoints'
import type {
  PaginatedResponse,
  Visitor,
  VisitorPurpose,
  VisitLog,
  VisitorSmsMessage,
  CreateVisitorPayload,
  UpdateVisitorPayload,
  LogVisitPayload,
  VisitorListParams,
  ExportVisitorsParams,
  CreatePurposePayload,
  UpdatePurposePayload,
  DuplicateCheckResult,
  WorkflowConfig,
  WorkflowTypeDefault,
  FieldDefinition,
  OrgVisitorFieldConfig,
  FieldConfigUpdate,
} from '@/types'

// ── Visitor CRUD ─────────────────────────────────────────────────────────────

export async function fetchVisitors(
  params: VisitorListParams,
): Promise<PaginatedResponse<Visitor>> {
  const res = await apiClient.get<PaginatedResponse<Visitor>>(API_ENDPOINTS.VISITORS.LIST, {
    params,
  })
  return res.data
}

export async function fetchVisitor(id: string): Promise<Visitor> {
  const res = await apiClient.get<{data: Visitor}>(API_ENDPOINTS.VISITORS.DETAIL(id))
  return res.data.data
}

export async function createVisitor(payload: CreateVisitorPayload): Promise<Visitor> {
  const res = await apiClient.post<{data: Visitor}>(API_ENDPOINTS.VISITORS.CREATE, payload)
  return res.data.data
}

export async function updateVisitor(
  id: string,
  payload: UpdateVisitorPayload,
): Promise<Visitor> {
  const res = await apiClient.patch<{data: Visitor}>(API_ENDPOINTS.VISITORS.UPDATE(id), payload)
  return res.data.data
}

export async function deleteVisitor(id: string): Promise<void> {
  await apiClient.delete(API_ENDPOINTS.VISITORS.DELETE(id))
}

// ── Visitor actions ──────────────────────────────────────────────────────────

export async function flagVisitor(id: string): Promise<Visitor> {
  const res = await apiClient.post<{data: Visitor}>(API_ENDPOINTS.VISITORS.FLAG(id))
  return res.data.data
}

export async function unflagVisitor(id: string): Promise<Visitor> {
  const res = await apiClient.post<{data: Visitor}>(API_ENDPOINTS.VISITORS.UNFLAG(id))
  return res.data.data
}

// ── Visit log ────────────────────────────────────────────────────────────────

export async function fetchVisitHistory(id: string): Promise<VisitLog[]> {
  const res = await apiClient.get<{data: { results: VisitLog[] } | VisitLog[]}>(API_ENDPOINTS.VISITORS.VISITS(id))
  const d = res.data.data
  return Array.isArray(d) ? d : (d as { results: VisitLog[] }).results ?? []
}

export async function logVisit(id: string, payload: LogVisitPayload): Promise<VisitLog> {
  const res = await apiClient.post<{data: VisitLog}>(API_ENDPOINTS.VISITORS.LOG_VISIT(id), payload)
  return res.data.data
}

// ── SMS history ──────────────────────────────────────────────────────────────

export async function fetchVisitorSmsHistory(id: string): Promise<VisitorSmsMessage[]> {
  const res = await apiClient.get<{data: { results: VisitorSmsMessage[] } | VisitorSmsMessage[]}>(API_ENDPOINTS.VISITORS.SMS_HISTORY(id))
  const d = res.data.data
  return Array.isArray(d) ? d : (d as { results: VisitorSmsMessage[] }).results ?? []
}

// ── Duplicate phone check ────────────────────────────────────────────────────

export async function checkPhoneNumber(phone: string): Promise<DuplicateCheckResult> {
  // Backend only handles GET with ?phone= query param (CheckPhoneView.get)
  const res = await apiClient.get<{ data: DuplicateCheckResult }>(
    API_ENDPOINTS.VISITORS.CHECK_PHONE,
    { params: { phone } },
  )
  return res.data.data
}

// ── Export ───────────────────────────────────────────────────────────────────

export async function exportVisitors(params: ExportVisitorsParams): Promise<void> {
  const fmt = params.format ?? 'csv'
  const res = await apiClient.get<Blob>(API_ENDPOINTS.VISITORS.EXPORT, {
    params,
    responseType: 'blob',
  })
  const ext = fmt === 'xlsx' ? 'xlsx' : 'csv'
  const url = URL.createObjectURL(res.data)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `visitors-${new Date().toISOString().slice(0, 10)}.${ext}`
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}

// ── SMS quick-send ────────────────────────────────────────────────────────────

export async function sendVisitorSms(
  visitorId: string,
  message: string,
): Promise<{ success: boolean; message_id: string }> {
  const res = await apiClient.post<{ data: { success: boolean; message_id: string } }>(
    API_ENDPOINTS.VISITORS.SEND_SMS(visitorId),
    { message },
  )
  return res.data.data
}

// ── Visitor Purposes ─────────────────────────────────────────────────────────

export async function fetchPurposes(): Promise<VisitorPurpose[]> {
  const res = await apiClient.get<{data: VisitorPurpose[]}>(API_ENDPOINTS.VISITORS.PURPOSES)
  return res.data.data
}

export async function createPurpose(payload: CreatePurposePayload): Promise<VisitorPurpose> {
  const res = await apiClient.post<{data: VisitorPurpose}>(API_ENDPOINTS.VISITORS.PURPOSES, payload)
  return res.data.data
}

export async function updatePurpose(
  id: string,
  payload: UpdatePurposePayload,
): Promise<VisitorPurpose> {
  const res = await apiClient.patch<{data: VisitorPurpose}>(
    API_ENDPOINTS.VISITORS.PURPOSE_DETAIL(id),
    payload,
  )
  return res.data.data
}

export async function activatePurpose(id: string): Promise<VisitorPurpose> {
  const res = await apiClient.patch<{data: VisitorPurpose}>(
    API_ENDPOINTS.VISITORS.PURPOSE_DETAIL(id),
    { is_active: true },
  )
  return res.data.data
}

export async function deactivatePurpose(id: string): Promise<VisitorPurpose> {
  const res = await apiClient.patch<{data: VisitorPurpose}>(
    API_ENDPOINTS.VISITORS.PURPOSE_DETAIL(id),
    { is_active: false },
  )
  return res.data.data
}

// ── Workflow config (V1.1) ────────────────────────────────────────────────────

export async function fetchWorkflowDefaults(): Promise<WorkflowTypeDefault[]> {
  const res = await apiClient.get<{ data: WorkflowTypeDefault[] }>(
    API_ENDPOINTS.VISITORS.WORKFLOW_DEFAULTS,
  )
  return res.data.data
}

export async function fetchWorkflowConfig(purposeId: string): Promise<WorkflowConfig> {
  const res = await apiClient.get<{ data: WorkflowConfig }>(
    API_ENDPOINTS.VISITORS.WORKFLOW_CONFIG(purposeId),
  )
  return res.data.data
}

export async function saveWorkflowConfig(
  purposeId: string,
  fields: FieldDefinition[],
): Promise<WorkflowConfig> {
  const res = await apiClient.patch<{ data: WorkflowConfig }>(
    API_ENDPOINTS.VISITORS.WORKFLOW_CONFIG(purposeId),
    { fields },
  )
  return res.data.data
}

export async function resetWorkflowConfig(purposeId: string): Promise<WorkflowConfig> {
  const res = await apiClient.delete<{ data: WorkflowConfig }>(
    API_ENDPOINTS.VISITORS.WORKFLOW_CONFIG(purposeId),
  )
  return res.data.data
}

// ── Photo capture (V1.2) ──────────────────────────────────────────────────────

export async function uploadVisitorPhoto(
  visitorId: string,
  photo: Blob,
): Promise<Visitor> {
  const form = new FormData()
  form.append('photo', photo, 'photo.jpg')
  const res = await apiClient.post<{ data: Visitor }>(
    API_ENDPOINTS.VISITORS.PHOTO(visitorId),
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return res.data.data
}

export async function removeVisitorPhoto(visitorId: string): Promise<Visitor> {
  const res = await apiClient.delete<{ data: Visitor }>(
    API_ENDPOINTS.VISITORS.PHOTO(visitorId),
  )
  return res.data.data
}

// ── Dynamic Field Registry (V1.5) ─────────────────────────────────────────────

/**
 * Fetch the enabled field schema for registration forms.
 * Returns an ordered list of enabled fields matching FieldDefinition shape.
 * GET /api/v1/visitors/field-schema/
 */
export async function fetchFieldSchema(): Promise<FieldDefinition[]> {
  const res = await apiClient.get<{ data: FieldDefinition[] }>(
    API_ENDPOINTS.VISITORS.FIELD_SCHEMA,
  )
  return res.data.data ?? []
}

/**
 * Fetch ALL field configs (enabled + disabled) for the settings page.
 * GET /api/v1/organizations/me/field-config/
 */
export async function fetchFieldConfig(): Promise<OrgVisitorFieldConfig[]> {
  const res = await apiClient.get<{ data: OrgVisitorFieldConfig[] }>(
    API_ENDPOINTS.ORGANIZATIONS.FIELD_CONFIG,
  )
  return res.data.data ?? []
}

/**
 * Bulk-update field configs.
 * PATCH /api/v1/organizations/me/field-config/
 * Body: FieldConfigUpdate[]  (raw JSON array — backend uses many=True)
 */
export async function updateFieldConfig(
  updates: FieldConfigUpdate[],
): Promise<OrgVisitorFieldConfig[]> {
  const res = await apiClient.patch<{ data: OrgVisitorFieldConfig[] }>(
    API_ENDPOINTS.ORGANIZATIONS.FIELD_CONFIG,
    updates,
  )
  return res.data.data ?? []
}
